<template>
	<div ref="container">
		<div v-if="loading" class="budget-chart__loading-container">
			<SuiLoadingCircle size="medium" />
		</div>
		<div v-else-if="!progress.length && project">
			<div v-if="isViewedInGrid" />
			<ProjectReportsChartEmptyState
				v-else
				:illustration-src="
					Assets.PROJECT_REPORTS_BUDGET_CHART_EMPTY_ILLUSTRATION
				"
				:content="t('project_reports_budget_chart_empty_state_content')"
				:project="project"
				:retainer-period-id="retainerPeriodId"
				icon="icon-chart-filled-24"
			/>
		</div>
		<div
			v-else
			:class="{ 'budget-chart__content-in-grid': isViewedInGrid }"
		>
			<ChartFilterBar class="budget-chart__filter-bar">
				<template #header>
					<h3>{{ $t('project_budget_chart') }}</h3>
					<SuiInfoButton
						v-if="!isViewedInGrid"
						as="a"
						:href="REPORTS_HELP_ARTICLE_URL"
					>
						{{ $t('project_learn_more') }}
					</SuiInfoButton>
				</template>

				<SuiCheckbox v-model="isShowingForecast">
					{{ $t('project_budget_chart_include_forecasted') }}
				</SuiCheckbox>
			</ChartFilterBar>

			<Chart
				:key="`chart-budget-${projectId}-${retainerPeriodId}`"
				:options="chartOptions"
				:class="className"
				v-bind="chartProps"
			/>
		</div>
	</div>
</template>

<script lang="ts" setup>
import { ChartFilterBar } from '@/features/App';
import { computed, ref, toRefs } from 'vue';
import { Chart } from 'highcharts-vue';
import Highcharts, { type Options } from 'highcharts';
import {
	SuiCheckbox,
	SuiInfoButton,
	SuiLoadingCircle,
} from '@developers/scoro-ui';
import { useFormatter, usePermissions, useTranslation } from '@/composables';
import { type UntypedLibrary } from '@/types/utils';
import {
	accessibilityForDuration,
	defaultOptions,
	plotOptionsDisableClickingOnLegend,
	type TooltipLine,
	tooltipShared,
	chartHighlightSeriesOnLegendHover,
	legendTranslated,
	stripedPattern,
	type YAxisOptionsParamsFunction,
	type MakeTooltipValueFormatterFunction,
} from '@/lib/highchartsUtils';
import { roundDurationToHours } from '@/utils/time';
import ProjectReportsChartEmptyState from '../ProjectReportsChartEmptyState.vue';
import { useElementSize, useStorage } from '@vueuse/core';
import { Assets } from '@/constants/assets';
import { useTrackChartInteractionEvent } from '../composables';
import { REPORTS_HELP_ARTICLE_URL } from '../../constants';
import { type ChartProject } from '../types';

import '@/lib/highchartsPlugins';
import { Setting } from '@/constants/settings';
import { Permission, PermissionAction } from '@/constants/permissions';
import { useBooleanSetting } from '@/composables/settings/useSettings';
import { BudgetChartLineNameTranslations } from '../constants';
import { type RetainerPeriod, type Project } from '@/generated/graphql';

const { t } = useTranslation();
const { formatLongDuration } = useFormatter();
const { can } = usePermissions();

const props = defineProps<{
	projectId: Project['id'];
	retainerPeriodId?: RetainerPeriod['id'];
	makeYAxisDurationOptions: YAxisOptionsParamsFunction;
	showPlanned: boolean;
	isViewedInGrid?: boolean;
	defaultMinYAxisSize?: number;
	project?: ChartProject;
	loading?: boolean;
	makeTooltipValueFormatter?: MakeTooltipValueFormatterFunction;
	chartUnitsLabel?: string;
	className: string;
}>();

const { project, loading } = toRefs(props);

const progress = computed(() => {
	// Sort the roles alphabetically
	return [...(project?.value?.progressData ?? [])].sort((a, b) =>
		a.groupName > b.groupName ? 1 : -1
	);
});

const isShowingForecast = useStorage('projectBudgetChartIncludeForecast', true);

const [isLaborCostEnabled] = useBooleanSetting(Setting.SHOW_EVENT_PRICE);

const canSeeOtherLaborCost = computed(() => {
	return can(
		PermissionAction.HAS_PERMISSION,
		Permission.CAN_SEE_OTHER_LABORCOST
	);
});

const canSeeMarginAndMarkup = computed(() => {
	return can(
		PermissionAction.HAS_PERMISSION,
		Permission.CAN_SEE_MARGIN_AND_MARKUP
	);
});

const canSeeCostAndProfitBreakdown = computed(() => {
	return (
		canSeeMarginAndMarkup.value &&
		(canSeeOtherLaborCost.value ||
			(!isLaborCostEnabled.value && !canSeeOtherLaborCost.value))
	);
});

/**
 * We do not want to show any budget lines if there are NO budgets set. If there
 * is at least one, then we want to show the lines and mark overtime as red.
 */
const haveAnyRevenueBudgets = computed(() => {
	return progress.value.some((oneUtilization) =>
		Boolean(oneUtilization.budgetValue)
	);
});
const haveAnyCostBudgets = computed(() => {
	return progress.value.some((oneUtilization) =>
		Boolean(oneUtilization.costBudget)
	);
});

const budgetValues = computed(() => {
	return progress.value.map((util) =>
		haveAnyRevenueBudgets.value ? util.budgetValue : null
	);
});

const costBudgetValues = computed(() => {
	return progress.value.map((util) =>
		haveAnyCostBudgets.value ? util.costBudget : null
	);
});

const actualValues = computed(() => {
	return progress.value.map((oneProgress) =>
		haveAnyRevenueBudgets.value
			? Math.min(oneProgress.actualValue, oneProgress.budgetValue)
			: oneProgress.actualValue
	);
});

const actualCostValues = computed(() => {
	return progress.value.map((oneProgress) =>
		haveAnyCostBudgets.value
			? Math.min(oneProgress.actualCost, oneProgress.costBudget)
			: oneProgress.actualCost
	);
});

const actualValuesOverBudget = computed(() => {
	return progress.value.map(
		(oneProgress, index) =>
			oneProgress.actualValue - actualValues.value[index]
	);
});

const actualCostValuesOverBudget = computed(() => {
	return progress.value.map(
		(oneProgress, index) =>
			oneProgress.actualCost - actualCostValues.value[index]
	);
});

const forecastedValues = computed(() => {
	return progress.value.map((oneProgress) =>
		haveAnyRevenueBudgets.value
			? Math.min(oneProgress.forecastedValue, oneProgress.budgetValue)
			: oneProgress.forecastedValue
	);
});

const forecastedCostValues = computed(() => {
	return progress.value.map((oneProgress) =>
		haveAnyCostBudgets.value
			? Math.min(oneProgress.forecastedCost, oneProgress.costBudget)
			: oneProgress.forecastedCost
	);
});

const forecastedValuesOverBudget = computed(() => {
	return progress.value.map((oneProgress, index) => {
		return oneProgress.forecastedValue - forecastedValues.value[index];
	});
});

const forecastedCostValuesOverBudget = computed(() => {
	return progress.value.map((oneProgress, index) => {
		return oneProgress.forecastedCost - forecastedCostValues.value[index];
	});
});

const plannedValues = computed(() => {
	return progress.value.map((oneProgress) =>
		haveAnyRevenueBudgets.value
			? Math.min(oneProgress.plannedValue, oneProgress.budgetValue)
			: oneProgress.plannedValue
	);
});

const plannedValuesOverBudget = computed(() => {
	return progress.value.map(
		(oneProgress, index) =>
			oneProgress.plannedValue - plannedValues.value[index]
	);
});

const container = ref<HTMLDivElement | null>(null);
const { width: containerWidth } = useElementSize(container);

/**
 * Calculate the `pointPlacement` for the bars.
 *
 * `pointPlacement` is a number that is usually -0.5 and 0.5 where 0 is the
 * center of the bar area and 0.5 is the right-most edge of the category. For
 * example, if a bar has 0.5, then it is on the right edge of the category area.
 *
 * Because the unit is a fraction of the category space, then we cannot
 * configure a specific pixel value to use as the offset. This means that if
 * there are more bars, then the same value would result in smaller pixel
 * values. The problem is that we want the columns to be offset by a specific nr
 * of pixels, not a fraction.
 *
 * Thus, we need to write some logic to intelligently set this fraction.
 *
 * https://api.highcharts.com/highcharts/series.column.pointPlacement
 */
const chartPointPlacement = computed(() => {
	let gapBetweenColumnLeftEdgesPx = -16;
	//If there is no planned column or no cost columns shown in revenue chart, then we have just one column and no need for chart point placement logic
	if (!props.showPlanned) {
		gapBetweenColumnLeftEdgesPx = 36;
		if (!canSeeCostAndProfitBreakdown.value) {
			return 0;
		}
	}
	// Prefer to use the axis line to get the chart width as it does not include
	// the left axis width. But if there is no axis yet, use the container div
	// for an approximation.
	const widthOfChart =
		containerWidth.value > 0
			? (container.value
					?.querySelector('.highcharts-axis-line')
					?.getBoundingClientRect().width ?? containerWidth.value)
			: containerWidth.value;
	if (!widthOfChart) {
		return 0;
	}

	const nrOfColumns = progress.value.length;
	const oneColumnWidth = widthOfChart / nrOfColumns;

	// Because both columns are moved away from each other, we divide the result by 2
	return gapBetweenColumnLeftEdgesPx / oneColumnWidth / 2;
});

// Add a custom pattern for Highcharts because we can't use the default ones in
// styled mode:
// https://www.highcharts.com/docs/chart-design-and-style/gradients-shadows-and-patterns
Highcharts.setOptions({
	defs: {
		forecastPattern: {
			...stripedPattern,
			id: 'budget-chart-forecast-pattern',
			// Highcharts type definitions don't accept an "id" even though the
			// examples use it...
		} as UntypedLibrary,
		// And a second one because we need to color the two patterns
		// differently.
		forecastPatternOverBudget: {
			...stripedPattern,
			id: 'budget-chart-forecast-pattern-over-budget',
		} as UntypedLibrary,
		forecastCostPattern: {
			...stripedPattern,
			id: 'budget-chart-cost-forecast-pattern',
			// Highcharts type definitions don't accept an "id" even though the
			// examples use it...
		} as UntypedLibrary,
	},
});

const maxAxisValue = computed(() => {
	const minAxisSize = props.defaultMinYAxisSize ?? 20 * 3600;

	const ret = Math.max(
		minAxisSize,
		...budgetValues.value.map((budget) => budget ?? 0),
		...costBudgetValues.value.map((budget) => budget ?? 0),
		...actualValues.value.map(
			(actualValue, index) =>
				actualValue + actualValuesOverBudget.value[index]
		),
		...forecastedValues.value.map(
			(forecastedValue, index) =>
				forecastedValue + forecastedValuesOverBudget.value[index]
		),
		...plannedValues.value.map(
			(plannedValue, index) =>
				plannedValue + plannedValuesOverBudget.value[index]
		),
		...actualCostValues.value.map(
			(actualCost, index) =>
				actualCost + actualCostValuesOverBudget.value[index]
		),
		...forecastedCostValues.value.map(
			(forecastedCostValues, index) =>
				forecastedCostValues +
				forecastedCostValuesOverBudget.value[index]
		)
	);

	return ret;
});

const secondsPerTick = computed(() => {
	if (props.defaultMinYAxisSize) {
		const step = maxAxisValue.value / 6;
		return step < 1000 ? 1000 : Math.round(step * 0.001) * 1000;
	}
	const hours = maxAxisValue.value < 100 * 3600 ? 10 : 20;

	return hours * 3600;
});

const chartOptions = computed<Options>(() =>
	defaultOptions({
		chart: chartHighlightSeriesOnLegendHover({
			linkedSeriesToHighlight: {
				project_budget_chart_forecasted: [
					'project_budget_chart_forecasted_over_budget',
				],
				project_budget_chart_actual: [
					'project_budget_chart_actual_over_budget',
				],
				project_budget_chart_planned: [
					'project_budget_chart_planned_over_budget',
				],
			},
		}),
		accessibility: accessibilityForDuration({
			formatLongDuration,
		}),
		plotOptions: plotOptionsDisableClickingOnLegend({
			column: {
				maxPointWidth: 24,
				grouping: false,
				stacking: 'normal',
				borderRadius: 4,
			},
		}),
		xAxis: {
			categories: progress.value.map(
				(utilization) => utilization.groupName
			),
		},
		yAxis: props.makeYAxisDurationOptions({
			formatLongDuration,
			maxAxisValue: maxAxisValue.value,
			secondsPerTick: secondsPerTick.value,
		}),
		legend: legendTranslated(),

		series: [
			// The Budget is basically a line graph where the line is hidden and the
			// markers are rectangles
			{
				name: BudgetChartLineNameTranslations.Budget,
				type: 'line',
				data: progress.value.map((util) =>
					haveAnyRevenueBudgets.value ? util.budgetValue : null
				),
				lineWidth: 0,
				pointPlacement: props.showPlanned
					? 0
					: -chartPointPlacement.value,
				marker: {
					symbol: 'rect',
					radius: props.showPlanned ? 20 : 11,
					lineWidth: 2,
					states: {
						hover: {
							fillColor: 'transparent',
						},
					},
				},
				states: {
					hover: {
						enabled: false,
						lineWidth: 0,
						lineWidthPlus: 0,
						halo: {
							enabled: false,
							opacity: 0,
						},
					},
				},
				zIndex: 10,
				legendIndex: 1,
				showInLegend: haveAnyRevenueBudgets.value,
			},
			{
				name: 'project_budget_chart_forecasted_over_budget',
				type: 'column',
				data: isShowingForecast.value
					? forecastedValuesOverBudget.value
					: [],
				pointPlacement: -chartPointPlacement.value,
				stack: 'forecasted',
				showInLegend: false,
				zIndex: 1,
			},
			{
				name: BudgetChartLineNameTranslations.Forecasted,
				type: 'column',
				data: isShowingForecast.value ? forecastedValues.value : [],
				pointPlacement: -chartPointPlacement.value,
				stack: 'forecasted',
				showInLegend: isShowingForecast.value,
				legendIndex: 3,
				zIndex: 2,
			},
			{
				name: 'project_budget_chart_actual_over_budget',
				type: 'column',
				data: actualValuesOverBudget.value,
				pointPlacement: -chartPointPlacement.value,
				stack: 'actual',
				showInLegend: false,
				zIndex: 3,
			},
			{
				name: BudgetChartLineNameTranslations.Actual,
				type: 'column',
				data: actualValues.value,
				pointPlacement: -chartPointPlacement.value,
				stack: 'actual',
				legendIndex: 2,
				zIndex: 4,
			},

			...(canSeeCostAndProfitBreakdown.value && !props.showPlanned
				? ([
						{
							name: 'project_budget_chart_forecasted_cost_over_budget',
							type: 'column',
							data: isShowingForecast.value
								? forecastedCostValuesOverBudget.value
								: [],
							pointPlacement: chartPointPlacement.value,
							stack: 'cost_forecasted',
							showInLegend: false,
							zIndex: 1,
						},
						{
							name: BudgetChartLineNameTranslations.CostForecasted,
							type: 'column',
							data: isShowingForecast.value
								? forecastedCostValues.value
								: [],
							pointPlacement: chartPointPlacement.value,
							showInLegend: isShowingForecast.value,
							stack: 'cost_forecasted',
							legendIndex: 6,
							zIndex: 2,
						},
						{
							name: 'project_budget_chart_actual_cost_over_budget',
							type: 'column',
							data: actualCostValuesOverBudget.value,
							pointPlacement: chartPointPlacement.value,
							stack: 'cost_actual',
							showInLegend: false,
							zIndex: 3,
						},
						{
							name: BudgetChartLineNameTranslations.CostBurn,
							type: 'column',
							data: actualCostValues.value,
							pointPlacement: chartPointPlacement.value,
							stack: 'cost_actual',
							legendIndex: 5,
							zIndex: 4,
						},
					] as Highcharts.SeriesOptionsType[])
				: []),
			...(!props.showPlanned && canSeeCostAndProfitBreakdown.value
				? ([
						{
							name: BudgetChartLineNameTranslations.TotalCost,
							type: 'line',
							data: progress.value.map((util) =>
								haveAnyCostBudgets.value
									? util.costBudget
									: null
							),
							lineWidth: 0,
							pointPlacement: chartPointPlacement.value,
							marker: {
								symbol: 'rect',
								radius: 11,
								lineWidth: 2,
								states: {
									hover: {
										fillColor: 'transparent',
									},
								},
							},
							states: {
								hover: {
									enabled: false,
									lineWidth: 0,
									lineWidthPlus: 0,
									halo: {
										enabled: false,
										opacity: 0,
									},
								},
							},
							zIndex: 10,
							legendIndex: 4,
							showInLegend: haveAnyCostBudgets.value,
						},
					] as Highcharts.SeriesOptionsType[])
				: []),
			...(props.showPlanned
				? ([
						{
							name: 'project_budget_chart_planned_over_budget',
							type: 'column',
							data: plannedValuesOverBudget.value,
							pointPlacement: chartPointPlacement.value,
							stack: 'planned',
							showInLegend: false,
						},
						{
							name: BudgetChartLineNameTranslations.Planned,
							type: 'column',
							showInLegend: props.showPlanned,
							data: plannedValues.value,
							pointPlacement: chartPointPlacement.value,
							stack: 'planned',
							zIndex: 1,
						},
					] as Highcharts.SeriesOptionsType[])
				: []),
		],

		tooltip: tooltipShared({
			getTitle: (x) => String(x),
			getLinesToShow(pointsByName): (TooltipLine | undefined)[] {
				const format = props.makeTooltipValueFormatter
					? props.makeTooltipValueFormatter()
					: (duration: number) => {
							return formatLongDuration({
								duration:
									duration >= 3600
										? roundDurationToHours(duration)
										: duration,
								removeMinutesOnFullHours: true,
								minutesUnitExtraShort: true,
								padMinutesIfNoHours: false,
							});
						};

				const budget = pointsByName.project_budget_chart_budget?.y;
				const costBudget =
					pointsByName.project_budget_chart_total_cost?.y;
				const planned =
					(pointsByName.project_budget_chart_planned?.y ?? 0) +
					(pointsByName.project_budget_chart_planned_over_budget?.y ??
						0);
				const actual =
					(pointsByName.project_budget_chart_actual?.y ?? 0) +
					(pointsByName.project_budget_chart_actual_over_budget?.y ??
						0);
				const forecasted =
					(pointsByName.project_budget_chart_forecasted?.y ?? 0) +
					(pointsByName.project_budget_chart_forecasted_over_budget
						?.y ?? 0);

				const actualCost =
					(pointsByName.project_budget_chart_actual_cost?.y ?? 0) +
					(pointsByName.project_budget_chart_actual_cost_over_budget
						?.y ?? 0);

				const forecastedCost =
					(pointsByName.project_budget_chart_forecasted_cost?.y ??
						0) +
					(pointsByName
						.project_budget_chart_forecasted_cost_over_budget?.y ??
						0);

				const getSecondaryValue = (duration: number) => {
					if (!budget || !duration) {
						return undefined;
					}

					const diff = duration - budget;

					if (diff <= 0) {
						return undefined;
					}

					return `+${format(
						Math.min(diff, duration),
						props.chartUnitsLabel
					)}`;
				};

				const getSecondaryCostValue = (duration: number) => {
					if (!costBudget || !duration) {
						return undefined;
					}

					const diff = duration - costBudget;
					if (diff <= 0) {
						return undefined;
					}

					return `+${format(
						Math.min(diff, duration),
						props.chartUnitsLabel
					)}`;
				};

				return [
					haveAnyRevenueBudgets.value
						? {
								label: t(
									BudgetChartLineNameTranslations.Budget
								),
								value: budget
									? format(budget, props.chartUnitsLabel)
									: t('n_a'),
								colorIndex: 0,
								type: 'line',
							}
						: undefined,
					...(haveAnyCostBudgets.value
						? ([
								{
									label: t(
										BudgetChartLineNameTranslations.TotalCost
									),
									value: costBudget
										? format(
												costBudget,
												props.chartUnitsLabel
											)
										: t('n_a'),
									colorIndex: 9,
									type: 'line',
								},
							] as TooltipLine[])
						: []),
					...(props.showPlanned
						? [
								{
									label: t(
										BudgetChartLineNameTranslations.Planned
									),
									value: format(
										planned,
										props.chartUnitsLabel
									),
									secondaryValue: getSecondaryValue(planned),
									colorIndex: 6,
								},
							]
						: []),
					{
						label: t(BudgetChartLineNameTranslations.Actual),
						value: format(actual, props.chartUnitsLabel),
						secondaryValue: getSecondaryValue(actual),
						colorIndex: 4,
					},
					...(isShowingForecast.value
						? [
								{
									label: t(
										BudgetChartLineNameTranslations.Forecasted
									),
									value: format(
										forecasted,
										props.chartUnitsLabel
									),
									secondaryValue:
										getSecondaryValue(forecasted),
									colorIndex: 2,
								},
							]
						: []),
					...(!props.showPlanned && canSeeCostAndProfitBreakdown.value
						? [
								{
									label: t(
										BudgetChartLineNameTranslations.CostBurn
									),
									value: format(
										actualCost,
										props.chartUnitsLabel
									),
									secondaryValue:
										getSecondaryCostValue(actualCost),
									colorIndex: 8,
								},
								...(isShowingForecast.value
									? [
											{
												label: t(
													BudgetChartLineNameTranslations.CostForecasted
												),
												value: format(
													forecastedCost,
													props.chartUnitsLabel
												),
												secondaryValue:
													getSecondaryCostValue(
														forecastedCost
													),
												colorIndex: 6,
											},
										]
									: []),
							]
						: []),
				];
			},
		}),
	})
);

const chartProps = useTrackChartInteractionEvent(
	'Project Bar Chart Interaction'
);
</script>

<style lang="scss" scoped>
.budget-chart {
	&__content-in-grid {
		padding: 3rem;
		border-radius: 0.5rem;
		box-shadow: $scoro-box-shadow;
	}

	&__filter-bar {
		margin-left: -3rem;
		margin-right: -3rem;
		padding-left: 3rem;
		padding-right: 3rem;
		margin-bottom: 5rem;
	}

	&__loading-container {
		display: flex;
		justify-content: center;
		padding-top: 3rem;
		padding-bottom: 3rem;
	}

	&__chart-revenue {
		--highcharts-color-0: #{$teal-60}; // revenue budget
		--highcharts-color-1: url(#budget-chart-forecast-pattern-over-budget); // forecast over budget, striped
		--highcharts-color-2: url(#budget-chart-forecast-pattern); // forecast, striped
		--highcharts-color-3: #{$red-50}; // actual revenue over budget
		--highcharts-color-4: #{$teal-40}; // actual revenue
		--highcharts-color-5: url(#budget-chart-forecast-pattern-over-budget); // cost forecasted over budget, striped
		--highcharts-color-6: url(#budget-chart-cost-forecast-pattern); // cost forecasted, striped
		--highcharts-color-7: #{$red-50}; // cost actual over budget
		--highcharts-color-8: #{$purple-40}; // cost actual
		--highcharts-color-9: #{$purple-60}; // cost budget
		--highcharts-halo-fill-opacity: 0;

		// Hide the line part of the budget graph
		--highcharts-graph-stroke-width: 0;
		--highcharts-point-hover-filter: none;
		--highcharts-series-inactive-opacity: 0;
		--highcharts-shared-tooltip-secondary-value-color: #{$red-50};
		--highcharts-legend-item-cursor: default;

		:deep(.highcharts-line-series .highcharts-point) {
			stroke-width: 2;
			stroke-linecap: round;
		}

		// Align the point in the legend to the center
		:deep(.highcharts-legend-item .highcharts-point) {
			transform: translateY(-1px);
		}

		:deep(#budget-chart-forecast-pattern-over-budget path) {
			stroke: $red-20;
		}

		:deep(#budget-chart-forecast-pattern path) {
			stroke: $blue-20;
		}

		:deep(#budget-chart-cost-forecast-pattern path) {
			stroke: $purple-20;
		}
	}

	&__chart-time {
		--highcharts-color-0: #{$teal-60}; // budget
		--highcharts-color-1: url(#budget-chart-forecast-pattern-over-budget);
		--highcharts-color-2: url(#budget-chart-forecast-pattern);
		--highcharts-color-3: #{$red-50}; // actual over budget
		--highcharts-color-4: #{$blue-60}; // actual
		--highcharts-color-5: #{$red-20}; // planned over budget
		--highcharts-color-6: #{$blue-30}; // planned
		--highcharts-halo-fill-opacity: 0;

		// Hide the line part of the budget graph
		--highcharts-graph-stroke-width: 0;
		--highcharts-point-hover-filter: none;
		--highcharts-series-inactive-opacity: 0;
		--highcharts-shared-tooltip-secondary-value-color: #{$red-50};
		--highcharts-legend-item-cursor: default;

		:deep(.highcharts-line-series .highcharts-point) {
			stroke-width: 2;
			stroke-linecap: round;
		}

		// Align the point in the legend to the center
		:deep(.highcharts-legend-item .highcharts-point) {
			transform: translateY(-1px);
		}

		:deep(#budget-chart-forecast-pattern-over-budget path) {
			stroke: $red-20;
		}

		:deep(#budget-chart-forecast-pattern path) {
			stroke: $blue-20;
		}
	}
}
</style>
